import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ExternalLink, Plus } from 'lucide-react';
import Toast, { ToastProps, ToastAction } from './Toast';
import './ToastContainer.css';

interface ToastContextType {
  showToast: (options: Omit<ToastProps, 'id' | 'onClose'>) => string;
  showSuccess: (
    title: string,
    message?: string,
    actions?: ToastAction[],
    duration?: number,
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' | 'center'
  ) => string;
  showError: (title: string, message?: string, actions?: ToastAction[]) => string;
  showInfo: (title: string, message?: string, actions?: ToastAction[]) => string;
  showWarning: (
    title: string,
    message?: string,
    actions?: ToastAction[],
    duration?: number,
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' | 'center'
  ) => string;
  showLeadCreated: (leadId: string, leadName: string, onViewLead?: () => void, onCreateTask?: () => void) => string;
  hideToast: (id: string) => void;
  hideAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' | 'center';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  position = 'top-right',
  maxToasts = 5 
}) => {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const showToast = useCallback((options: Omit<ToastProps, 'id' | 'onClose'>) => {
    const id = generateId();
    const desiredPosition = (options as any).position ?? position;
    const newToast = {
      ...options,
      id,
      position: desiredPosition,
      onClose: (toastId: string) => hideToast(toastId)
    };

    setToasts(prevToasts => {
      const updatedToasts = [newToast, ...prevToasts];
      // Limiter le nombre de toasts
      return updatedToasts.slice(0, maxToasts);
    });

    return id;
  }, [position, maxToasts]);

  const hideToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const hideAll = useCallback(() => {
    setToasts([]);
  }, []);

  const showSuccess = useCallback((
    title: string,
    message?: string,
    actions?: ToastAction[],
    duration: number = 5000,
    positionOverride?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' | 'center'
  ) => {
    return showToast({ title, message, type: 'success', actions, duration, position: positionOverride });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, actions?: ToastAction[]) => {
    return showToast({ title, message, type: 'error', actions });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, actions?: ToastAction[]) => {
    return showToast({ title, message, type: 'info', actions });
  }, [showToast]);

  const showWarning = useCallback((
    title: string,
    message?: string,
    actions?: ToastAction[],
    duration: number = 5000,
    positionOverride?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' | 'center'
  ) => {
    return showToast({ title, message, type: 'warning', actions, duration, position: positionOverride });
  }, [showToast]);

  // Méthode spécialisée pour la création de leads
  const showLeadCreated = useCallback((
    leadId: string, 
    leadName: string,
    onViewLead?: () => void,
    onCreateTask?: () => void
  ) => {
    const actions: ToastAction[] = [];
    
    if (onViewLead) {
      actions.push({
        label: 'Ouvrir la fiche',
        onClick: onViewLead,
        variant: 'primary',
        icon: <ExternalLink size={14} />
      });
    }
    
    if (onCreateTask) {
      actions.push({
        label: 'Créer une tâche',
        onClick: onCreateTask,
        variant: 'secondary',
        icon: <Plus size={14} />
      });
    }

    return showToast({
      title: 'Lead créé avec succès !',
      message: `Le lead "${leadName}" a été enregistré en base de données.`,
      type: 'success',
      duration: 7000, // Plus long pour les actions importantes
      actions
    });
  }, [showToast]);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showLeadCreated,
    hideToast,
    hideAll
  };

  const positions = [
    'top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center', 'center'
  ] as const;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {positions.map(pos => (
        <div key={pos} className={`toast-container ${pos}`}>
          {toasts
            .filter(t => (t.position || position) === pos)
            .map(toast => (
              <Toast key={toast.id} {...toast} />
            ))}
        </div>
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Hook pour créer des notifications de leads avec des actions prédéfinies
export const useLeadNotifications = () => {
  const { showLeadCreated, showError } = useToast();

  const notifyLeadCreated = useCallback((
    leadId: string,
    leadName: string,
    navigateToLead?: (id: string) => void,
    openTaskModal?: (leadId: string) => void
  ) => {
    return showLeadCreated(
      leadId,
      leadName,
      navigateToLead ? () => navigateToLead(leadId) : undefined,
      openTaskModal ? () => openTaskModal(leadId) : undefined
    );
  }, [showLeadCreated]);

  const notifyLeadError = useCallback((error: string) => {
    return showError(
      'Échec de création',
      `Impossible d'enregistrer le lead. ${error || 'Veuillez réessayer.'}`
    );
  }, [showError]);

  return {
    notifyLeadCreated,
    notifyLeadError
  };
};

export default ToastProvider;